import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useAuth } from 'react-oidc-context';
import {
    feetAndInchesToDecimalFeet,
    isFeetInchesNumberChallenge,
    normalizeFeetAndInches,
    roundToDecimalPlaces,
} from '@moment-golf/shared';
import type { challengeSubmission, EventScoreWithId, TbRegistration } from '@moment-golf/types';
import CreateApi from '../../../../shared/Settings/appSettings';
import {
    type ChallengeGroupPathEntry,
    type ChallengePlayerOption,
    getPlayersWithoutChallengeSubmission,
    resolvePlayerEventScore,
    upsertChallengeSubmissionOnScore,
} from '../../../utils/challengeCommandCenterHelpers';

export interface ChallengeConfigRow {
    name?: string;
    type?: string;
    hole?: unknown;
    inputType?: string;
    units?: string;
    decimalPlaces?: number;
}

interface AddChallengeResultDialogProps {
    open: boolean;
    onClose: () => void;
    challenge: ChallengeConfigRow;
    roundNumber: number;
    registrations: TbRegistration[];
    scores: EventScoreWithId[];
    groupPath?: ChallengeGroupPathEntry[];
    onSaved: (updatedScores: EventScoreWithId[]) => void;
}

export const AddChallengeResultDialog = ({
    open,
    onClose,
    challenge,
    roundNumber,
    registrations,
    scores,
    groupPath,
    onSaved,
}: AddChallengeResultDialogProps) => {
    const auth = useAuth();
    const api = CreateApi();

    const challengeName =
        challenge.name != null && String(challenge.name).trim() !== ''
            ? String(challenge.name)
            : String(challenge.type ?? '');
    const challengeHole = Number(challenge.hole);

    const useFeetInches = isFeetInchesNumberChallenge(challenge);
    const decimalPlaces = useMemo(() => {
        const p = Number(challenge.decimalPlaces);
        return Number.isFinite(p) ? p : 1;
    }, [challenge.decimalPlaces]);

    const playerOptions = useMemo(
        () =>
            getPlayersWithoutChallengeSubmission(
                registrations,
                scores,
                roundNumber,
                challengeName,
                challengeHole,
                groupPath,
            ),
        [registrations, scores, roundNumber, challengeName, challengeHole, groupPath],
    );

    const [selectedPlayer, setSelectedPlayer] = useState<ChallengePlayerOption | null>(null);
    const [challengeScore, setChallengeScore] = useState(0);
    const [challengeFeet, setChallengeFeet] = useState(0);
    const [challengeInches, setChallengeInches] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSelectedPlayer(null);
        setChallengeScore(0);
        setChallengeFeet(0);
        setChallengeInches(0);
        setError(null);
        setSaving(false);
    }, [open, challengeName, challengeHole, groupPath]);

    const handleSave = async () => {
        if (!selectedPlayer) {
            setError('Select a player');
            return;
        }

        const resolved = resolvePlayerEventScore(
            selectedPlayer.label,
            registrations,
            scores,
            roundNumber,
        );
        if (!resolved) {
            setError('No event score found for this player');
            return;
        }

        const rawNumber = useFeetInches
            ? feetAndInchesToDecimalFeet(challengeFeet, challengeInches)
            : challengeScore;
        const value =
            challenge.inputType === 'number'
                ? roundToDecimalPlaces(rawNumber, decimalPlaces)
                : true;

        const submission: challengeSubmission = {
            challengeName,
            challengeHole,
            playerName: selectedPlayer.label,
            value,
            submittedAt: new Date(),
            submittedBy: auth.user?.profile.sub ?? 'commandCenter',
        };

        const updatedScores: EventScoreWithId[] = JSON.parse(JSON.stringify(scores));
        const scoreIdx = updatedScores.findIndex((s) => String(s._id) === resolved.eventScoreID);
        if (scoreIdx < 0) {
            setError('Event score document not found');
            return;
        }

        const updatedChallenges = upsertChallengeSubmissionOnScore(
            updatedScores[scoreIdx],
            resolved.roundIndex,
            submission,
        );

        setSaving(true);
        setError(null);
        try {
            await api.post('/ls/UpdateChallengeScores', {
                eventScoreID: resolved.eventScoreID,
                roundIndex: resolved.roundIndex,
                challengeSubmissions: updatedChallenges,
                userID: auth.user?.profile.sub ?? 'commandCenter',
            });
            onSaved(updatedScores);
            onClose();
        } catch (err) {
            console.error('Error saving challenge result', err);
            setError('Failed to save challenge result');
        } finally {
            setSaving(false);
        }
    };

    const unitsLabel = String(challenge.units ?? '').trim();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Add Challenge Result</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {challengeName} — Hole {challengeHole}
                        {groupPath?.length
                            ? ` (${groupPath.map((p) => p.label).join(' / ')})`
                            : ''}
                    </Typography>
                    <Autocomplete
                        options={playerOptions}
                        getOptionLabel={(option) => option.label}
                        value={selectedPlayer}
                        onChange={(_e, value) => setSelectedPlayer(value)}
                        isOptionEqualToValue={(a, b) => a.label === b.label}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Player"
                                error={!selectedPlayer}
                                helperText={
                                    playerOptions.length === 0
                                        ? 'All eligible players already have a submission'
                                        : undefined
                                }
                            />
                        )}
                    />
                    {challenge.inputType === 'number' && !useFeetInches && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                type="number"
                                label="Value"
                                value={challengeScore}
                                onChange={(e) => setChallengeScore(Number(e.target.value))}
                                onFocus={(e) => e.target.select()}
                                sx={{ flex: 1 }}
                            />
                            {unitsLabel ? (
                                <Typography variant="body2">{unitsLabel}</Typography>
                            ) : null}
                        </Stack>
                    )}
                    {challenge.inputType === 'number' && useFeetInches && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                type="number"
                                label="Ft"
                                value={challengeFeet}
                                onChange={(e) => setChallengeFeet(Number(e.target.value))}
                                onBlur={() => {
                                    const n = normalizeFeetAndInches(challengeFeet, challengeInches);
                                    setChallengeFeet(n.feet);
                                    setChallengeInches(n.inches);
                                }}
                                onFocus={(e) => e.target.select()}
                                sx={{ width: 100 }}
                            />
                            <TextField
                                type="number"
                                label="In"
                                value={challengeInches}
                                onChange={(e) => setChallengeInches(Number(e.target.value))}
                                onBlur={() => {
                                    const n = normalizeFeetAndInches(challengeFeet, challengeInches);
                                    setChallengeFeet(n.feet);
                                    setChallengeInches(n.inches);
                                }}
                                onFocus={(e) => e.target.select()}
                                sx={{ width: 100 }}
                            />
                        </Stack>
                    )}
                    {challenge.inputType !== 'number' && (
                        <Typography variant="body2" color="text.secondary">
                            This challenge will be recorded as submitted (true).
                        </Typography>
                    )}
                    {error ? (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !selectedPlayer || playerOptions.length === 0}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
